import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPlantById, PLANTS } from '@/data/plants';
import { DAY_TYPE_META } from '@/types/biodynamic';
import { useAuth } from '@/context/AuthContext';
import { useGarden } from '@/context/GardenContext';
import AddPlantModal from '@/components/garden/AddPlantModal';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useHemisphere } from '@/context/HemisphereContext';
import './PlantDetailPage.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PlantDetailPage() {
  const { dataMonth, isSouthern } = useHemisphere();
  const { id } = useParams<{ id: string }>();
  const plant = id ? getPlantById(id) : undefined;
  const [activeTab, setActiveTab] = useState<'cultivation' | 'harvesting' | 'pests' | 'biodynamic'>('cultivation');

  usePageMeta(
    plant ? `How to Grow ${plant.name}` : 'Plant Not Found',
    plant
      ? `How to grow ${plant.name} (${plant.latinName}) organically — sowing and harvesting months, companion plants, common pests and diseases, and biodynamic timing.`
      : 'That plant could not be found. Browse the full growing guide index instead.',
    // Purpose-made 1200x630 card, not the 481x184 in-page photo.
    plant ? `/og/plant-${plant.id}.png` : undefined,
  );

  if (!plant) {
    return (
      <div className="container" style={{ padding: 'var(--space-20) 0', textAlign: 'center' }}>
        <p className="text-headline">Plant not found</p>
        <Link to="/plants" className="btn-sage" style={{ marginTop: 'var(--space-6)', display: 'inline-flex' }}>← Back to plants</Link>
      </div>
    );
  }

  const { isAuthenticated } = useAuth();
  const { garden } = useGarden();
  const [showAddModal, setShowAddModal] = useState(false);

  const dayMeta = DAY_TYPE_META[plant.biodynamicDay];
  const companions = plant.companions.map(id => PLANTS.find(p => p.id === id)).filter(Boolean);
  const alreadyInGarden = garden?.plants.some(p => p.plantId === plant.id) ?? false;

  return (
    <div className="plant-detail">
      {/* Hero */}
      <div className="plant-detail-hero">
        <div className="container">
          <nav className="breadcrumb text-caption" aria-label="Breadcrumb">
            <Link to="/plants">Plants</Link>
            <span aria-hidden="true"> / </span>
            <span>{plant.name}</span>
          </nav>

          <div className="plant-detail-hero-inner">
            <div className="plant-detail-visual">
              {plant.imageUrl
                ? <img src={plant.imageUrl} alt={plant.name} className="plant-detail-photo" />
                : (
                  <div className="plant-detail-emoji-wrap">
                    <span className="plant-detail-emoji">{plant.emoji}</span>
                  </div>
                )
              }
            </div>

            <div className="plant-detail-intro">
              <div className="plant-detail-meta">
                <span className={`badge badge-${plant.biodynamicDay === 'root' ? 'root' : plant.biodynamicDay === 'flower' ? 'flower' : plant.biodynamicDay === 'fruit' ? 'fruit' : 'leaf'}`}>
                  {dayMeta.emoji} {dayMeta.label}
                </span>
                <span className="plant-detail-difficulty text-caption">
                  {plant.difficulty === 'easy' ? '😊 Easy' : plant.difficulty === 'moderate' ? '🤔 Moderate' : '💪 Challenging'}
                </span>
                <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>{plant.family}</span>
              </div>

              <h1 className="plant-detail-title text-display">{plant.name}</h1>
              <p className="plant-detail-latin text-subtitle">{plant.latinName}</p>
              <p className="plant-detail-desc">{plant.description}</p>

              {isAuthenticated && (
                <button
                  className="btn-sage"
                  onClick={() => setShowAddModal(true)}
                  disabled={alreadyInGarden}
                  style={{ marginTop: 'var(--space-5)', opacity: alreadyInGarden ? 0.6 : 1 }}
                >
                  {alreadyInGarden ? '✓ In my garden' : '+ Add to my garden'}
                </button>
              )}

              {/* Key facts */}
              <div className="plant-key-facts">
                <div className="plant-key-fact">
                  <span className="text-label">Sowing</span>
                  <span className="text-hand">{plant.sowingMethod === 'direct' ? 'Direct sow' : plant.sowingMethod === 'transplant' ? 'Start indoors' : 'Both methods'}</span>
                </div>
                <div className="plant-key-fact">
                  <span className="text-label">Sunlight</span>
                  <span className="text-hand">{plant.sunlight === 'full' ? '☀️ Full sun' : plant.sunlight === 'partial' ? '⛅ Partial' : '🌑 Shade'}</span>
                </div>
                <div className="plant-key-fact">
                  <span className="text-label">Water</span>
                  <span className="text-hand">{plant.waterNeeds === 'low' ? '💧 Low' : plant.waterNeeds === 'moderate' ? '💧💧 Moderate' : '💧💧💧 High'}</span>
                </div>
                <div className="plant-key-fact">
                  <span className="text-label">Days to harvest</span>
                  <span className="text-hand">{plant.daysToHarvest.min}–{plant.daysToHarvest.max}</span>
                </div>
                <div className="plant-key-fact">
                  <span className="text-label">Soil pH</span>
                  <span className="text-hand">{plant.soilPH.min}–{plant.soilPH.max}</span>
                </div>
                <div className="plant-key-fact">
                  <span className="text-label">Spacing</span>
                  <span className="text-hand">{plant.spacingCm.plant}cm × {plant.spacingCm.row}cm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container plant-detail-body">
        <div className="plant-detail-layout">

          {/* Main content */}
          <div className="plant-detail-main">
            {/* Calendar */}
            <div className="plant-section paper">
              <h2 className="plant-section-title text-title">Planting Calendar</h2>
              {isSouthern && (
                <p className="text-caption" style={{ color: 'var(--ink-faint)', marginBottom: 'var(--space-3)' }}>
                  🌏 Adjusted for southern hemisphere seasons.
                </p>
              )}
              <div className="plant-calendar-full">
                <div className="plant-calendar-legend">
                  <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: 'var(--ochre-light)' }}></span>Sow indoors</span>
                  <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: 'var(--sage-light)' }}></span>Sow outdoors</span>
                  <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: 'var(--sky)' }}></span>Transplant</span>
                  <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: 'var(--terracotta)' }}></span>Harvest</span>
                </div>
                <div className="plant-cal-strip">
                  {MONTHS.map((m, i) => {
                    // Columns stay in the reader's calendar order; the data
                    // behind them shifts six months below the equator.
                    const month = dataMonth(i + 1);
                    const c = plant.calendar;
                    const isSowIn = c.sowIndoors?.includes(month);
                    const isSowOut = c.sowOutdoors?.includes(month);
                    const isTransplant = c.transplant?.includes(month);
                    const isHarvest = c.harvest?.includes(month);
                    return (
                      <div key={m} className="plant-cal-month">
                        <span className="plant-cal-label text-caption">{m}</span>
                        {isSowIn      && <div className="plant-cal-bar plant-cal-bar--sow-in" title="Sow indoors" />}
                        {isSowOut     && <div className="plant-cal-bar plant-cal-bar--sow-out" title="Sow outdoors" />}
                        {isTransplant && <div className="plant-cal-bar plant-cal-bar--transplant" title="Transplant" />}
                        {isHarvest    && <div className="plant-cal-bar plant-cal-bar--harvest" title="Harvest" />}
                        {!isSowIn && !isSowOut && !isTransplant && !isHarvest && <div className="plant-cal-bar plant-cal-bar--none" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="plant-tabs">
              {([
                { key: 'cultivation', label: 'Cultivation', emoji: '🌱' },
                { key: 'harvesting',  label: 'Harvesting',  emoji: '✂️' },
                { key: 'pests',       label: 'Pests & Disease', emoji: '🐛' },
                { key: 'biodynamic',  label: 'Biodynamic',  emoji: '🌕' },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  className={`plant-tab ${activeTab === tab.key ? 'plant-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.emoji} {tab.label}
                </button>
              ))}
            </div>

            <div className="plant-tab-content paper">
              {activeTab === 'cultivation' && (
                <div className="prose">
                  <div dangerouslySetInnerHTML={{ __html: plant.cultivation.replace(/## (.+)/g, '<h2>$1</h2>').replace(/\n\n/g, '</p><p>').replace(/^/, '<p>').replace(/$/, '</p>') }} />
                  {plant.soilType && (
                    <div className="note">
                      <strong>Soil:</strong> {plant.soilType}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'harvesting' && (
                <div className="prose">
                  <p>{plant.harvesting}</p>
                  {plant.storage && (
                    <div className="note">
                      <strong>Storage:</strong> {plant.storage}
                    </div>
                  )}
                  {plant.nutritionFacts && (
                    <div style={{ marginTop: 'var(--space-6)' }}>
                      <h3>Nutrition</h3>
                      <p>{plant.nutritionFacts}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'pests' && (
                <div className="prose">
                  <h2>Common Pests</h2>
                  <ul>{plant.pests.map(p => <li key={p}>{p}</li>)}</ul>
                  <h2>Diseases</h2>
                  <ul>{plant.diseases.map(d => <li key={d}>{d}</li>)}</ul>
                  <h2>Organic Solutions</h2>
                  <ul>{plant.organicTips.map(t => <li key={t}>{t}</li>)}</ul>
                  {plant.historicalNote && (
                    <div className="warning" style={{ marginTop: 'var(--space-6)' }}>
                      <strong>Historical note:</strong> {plant.historicalNote}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'biodynamic' && (
                <div className="prose">
                  <h2>{dayMeta.emoji} {dayMeta.label}</h2>
                  <p>This plant is best sown, cultivated, and harvested on <strong>{dayMeta.label}s</strong> — {dayMeta.description}.</p>
                  <p>On a {plant.biodynamicDay} day, the forces that support {plant.biodynamicDay} development are at their strongest according to biodynamic practice. This is the ideal time to sow, transplant, water, weed, or harvest this plant.</p>
                  <div className="note">
                    <strong>In practice:</strong> When the moon is in a {plant.biodynamicDay} constellation (as shown in the biodynamic calendar), activities relating to the {plant.biodynamicDay} part of the plant will give the best results.
                  </div>
                  <p>
                    <Link to="/biodynamic" style={{ color: 'var(--sage)', fontWeight: 600 }}>
                      View the biodynamic calendar →
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="plant-detail-sidebar">

            {/* Companions */}
            {companions.length > 0 && (
              <div className="plant-sidebar-card paper">
                <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Good companions</h3>
                <div className="companion-list">
                  {companions.map(c => c && (
                    <Link key={c.id} to={`/plants/${c.id}`} className="companion-item">
                      <span className="companion-emoji">{c.emoji}</span>
                      <span className="text-hand">{c.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="plant-sidebar-card paper">
              <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Tags</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {plant.tags.map(tag => (
                  <span key={tag} className="plant-tag text-caption" style={{ padding: 'var(--space-1) var(--space-3)', fontSize: '0.8rem' }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Back link */}
            <Link to="/plants" className="text-small" style={{ color: 'var(--sage)', display: 'block', marginTop: 'var(--space-2)' }}>
              ← Back to all plants
            </Link>
          </aside>
        </div>
      </div>

      {showAddModal && (
        <AddPlantModal plant={plant} onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}
